import {CdkStep} from '@angular/cdk/stepper';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, viewChild, inject } from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {BillingInfoComponent} from '@app/checkout/components/billing-info/billing-info.component';
import {ConsentInfoComponent} from '@app/checkout/components/consent-info/consent-info.component';
import {OrderConfirmComponent} from '@app/checkout/components/order-confirm/order-confirm.component';
import {OrderInfoComponent} from '@app/checkout/components/order-info/order-info.component';
import {PatientInfoComponent} from '@app/checkout/components/patient-info/patient-info.component';
import {ECheckoutStatus} from '@app/checkout/enums/checkout-status.enum';
import {CheckoutStep} from '@app/checkout/enums/checkout-step.enum';
import {ICancelOrderState} from '@app/checkout/models/cancel-order-state.model';
import {Order} from '@app/checkout/models/order-request.model';
import {ICheckoutStep} from '@app/checkout/models/order-step.model';
import {CheckoutService} from '@app/checkout/services/checkout.service';
import {cancelOrderAction} from '@app/checkout/state/checkout.actions';
import {selectCancelOrder, selectOrderData, selectOrderForm} from '@app/checkout/state/checkout.selector';
import {StepperComponent} from '@app/shared/components/stepper/stepper.component';
import {Store} from '@ngrx/store';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {ConfirmationService} from 'primeng/api';
import {Observable, Subject, takeUntil, tap} from 'rxjs';
import {StepperComponent as StepperComponent_1} from '../../../shared/components/stepper/stepper.component';
import {BillingInfoComponent as BillingInfoComponent_1} from '../billing-info/billing-info.component';
import {ClinicalInfoComponent} from '../clinical-info/clinical-info.component';
import {ConsentInfoComponent as ConsentInfoComponent_1} from '../consent-info/consent-info.component';
import {OrderConfirmComponent as OrderConfirmComponent_1} from '../order-confirm/order-confirm.component';
import {OrderInfoComponent as OrderInfoComponent_1} from '../order-info/order-info.component';
import {PatientInfoComponent as PatientInfoComponent_1} from '../patient-info/patient-info.component';

@Component({
  selector: 'app-checkout-stepper',
  templateUrl: './checkout-stepper.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    StepperComponent_1,
    CdkStep,
    OrderInfoComponent_1,
    BillingInfoComponent_1,
    PatientInfoComponent_1,
    ClinicalInfoComponent,
    ConsentInfoComponent_1,
    OrderConfirmComponent_1,
    TranslatePipe,
  ],
})
export class CheckoutStepperComponent implements AfterViewInit {
  private readonly store = inject(Store);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly checkoutService = inject(CheckoutService);
  private readonly route = inject(ActivatedRoute);

  orderInfoComponent = viewChild(OrderInfoComponent);
  billingInfoComponent = viewChild(BillingInfoComponent);
  patientInfoComponent = viewChild(PatientInfoComponent);
  clinicalInfoComponent = viewChild(ClinicalInfoComponent);
  consentInfoComponent = viewChild(ConsentInfoComponent);
  orderConfirmComponent = viewChild(OrderConfirmComponent);
  stepperComponent = viewChild(StepperComponent);
  checkoutState: ECheckoutStatus | undefined;
  orderData: Order | undefined;
  steps: ICheckoutStep[] = [];
  checkoutStep = CheckoutStep;
  private readonly unsubscribe$ = new Subject<void>();
  private orderId = 0;
  cancelOrderState$: Observable<ICancelOrderState>;
  viewInitialized = false;

  constructor() {
    this.store
      .select(selectOrderForm)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((order: Order) => {
          this.orderData = order;
        })
      )
      .subscribe();

    this.cancelOrderState$ = this.store.select(selectCancelOrder);
  }

  ngAfterViewInit() {
    this.viewInitialized = true;
    this.route.queryParams?.subscribe(params => {
      if (params['id'] && params['id'] > 0) {
        this.orderId = params['id'];
      }
    });

    this.store
      .select(selectOrderData)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(orderData => {
          if (this.orderId == orderData?.id) {
            const checkoutOrder = this.checkoutService.getCheckoutStepsStatus(
              orderData.lastCompletedStep,
              orderData.currentStep ? orderData.currentStep : orderData.lastCompletedStep
            );
            this.steps = checkoutOrder.steps;
            if (
              this.stepperComponent()?.selectedIndex === 0 &&
              checkoutOrder.currentStep === checkoutOrder.lastCompletedStep
            ) {
              const stepper = this.stepperComponent();
              if (stepper) {
                stepper.selectedIndex = checkoutOrder.lastCompletedStep + 1;
              }
            }
            this.changeDetectorRef.detectChanges();
          }
        })
      )
      .subscribe();
  }

  nextStepDisabled() {
    const component = this.getComponent();
    if (component) {
      return component.disableNext();
    }
    return true;
  }

  nextStep() {
    const component = this.getComponent();
    if (component) {
      component.next();
    }
  }

  previousStep() {
    const component = this.getComponent();
    if (component) {
      component.previous();
    }
  }

  cancelOrder() {
    this.confirmationService.confirm({
      message: this.translate.instant('Checkout.CancelOrderMessage'),
      header: this.translate.instant('Checkout.CancelOrderHeader'),
      acceptIcon: 'pi pi-check',
      acceptLabel: this.translate.instant('Checkout.Yes'),
      rejectIcon: 'pi pi-times',
      rejectLabel: this.translate.instant('Checkout.No'),
      icon: 'pi pi-info-circle',
      accept: () => {
        this.store.dispatch(cancelOrderAction({request: {id: this.orderData?.id ?? 0}}));
      },
    });
  }

  draftOrSubmitOrder() {
    const component = this.getComponent();
    if (component) {
      component.saveDraftOrder();
    }
  }

  isNextLoading() {
    const component = this.getComponent();
    if (component) {
      return component.nextLoading();
    }
    return false;
  }

  isPreviousLoading() {
    const component = this.getComponent();
    if (component) {
      return component.previousLoading();
    }
    return false;
  }

  isCancelLoading() {
    const component = this.getComponent();
    if (component) {
      return component.cancelLoading();
    }
    return false;
  }

  isDraftOrSubmitLoading() {
    const component = this.getComponent();
    if (component) {
      component.draftOrSubmitLoading();
    }
    return false;
  }

  getOrderStepStatus(stepId: CheckoutStep): boolean {
    return this.steps.find(t => t.stepId == stepId)?.enabled ?? false;
  }

  private getComponent() {
    if (this.stepperComponent()?.selectedIndex === CheckoutStep.OrderInfo) {
      return this.orderInfoComponent();
    } else if (this.stepperComponent()?.selectedIndex === CheckoutStep.BillingInfo) {
      return this.billingInfoComponent();
    } else if (this.stepperComponent()?.selectedIndex === CheckoutStep.PatientInfo) {
      return this.patientInfoComponent();
    } else if (this.stepperComponent()?.selectedIndex === CheckoutStep.ClinicalHistory) {
      return this.clinicalInfoComponent();
    } else if (this.stepperComponent()?.selectedIndex === CheckoutStep.ConsentInfo) {
      return this.consentInfoComponent();
    } else if (this.stepperComponent()?.selectedIndex === CheckoutStep.ConfirmOrder) {
      return this.orderConfirmComponent();
    }
    return null;
  }
}
